import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Workflow as WorkflowIcon, Trash2, Play } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Workflows = () => {
  const { getAuthHeader } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const workflowTemplates = [
    {
      name: 'Buyer Qualification Flow',
      description: 'Qualify potential property buyers with budget, timeline, and preferences',
      nodes: [
        { type: 'start', content: 'Greet and introduce' },
        { type: 'question', content: 'What is your budget range?' },
        { type: 'question', content: 'When are you looking to buy?' },
        { type: 'question', content: 'Preferred locations in Saint John?' },
        { type: 'action', content: 'Schedule consultation' },
        { type: 'end', content: 'Thank and confirm' }
      ]
    },
    {
      name: 'Seller Qualification Flow',
      description: 'Qualify property sellers with property details and selling timeline',
      nodes: [
        { type: 'start', content: 'Greet and confirm interest' },
        { type: 'question', content: 'What type of property are you selling?' },
        { type: 'question', content: 'What is your expected timeline?' },
        { type: 'question', content: 'Have you listed with an agent before?' },
        { type: 'action', content: 'Schedule property assessment' },
        { type: 'end', content: 'Thank and confirm' }
      ]
    },
    {
      name: 'Investment Property Flow',
      description: 'Qualify real estate investors with portfolio and investment criteria',
      nodes: [
        { type: 'start', content: 'Greet and introduce investment options' },
        { type: 'question', content: 'Current investment portfolio?' },
        { type: 'question', content: 'Investment budget available?' },
        { type: 'question', content: 'Preferred property types?' },
        { type: 'action', content: 'Share investment opportunities' },
        { type: 'end', content: 'Schedule investment consultation' }
      ]
    }
  ];

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/workflows`, {
        headers: getAuthHeader()
      });
      setWorkflows(response.data);
    } catch (error) {
      toast.error('Failed to fetch workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/workflows`, formData, {
        headers: getAuthHeader()
      });
      toast.success('Workflow created successfully');
      setDialogOpen(false);
      setFormData({ name: '', description: '' });
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to create workflow');
    }
  };

  const createTemplateWorkflow = async (template) => {
    try {
      await axios.post(
        `${API}/workflows`,
        {
          name: template.name,
          description: template.description
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Created ${template.name}`);
      fetchWorkflows();
    } catch (error) {
      toast.error('Failed to create workflow');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-600">Loading workflows...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Workflows</h1>
          <p className="text-slate-600">Design conversation flows and automation sequences</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-workflow-button"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Workflow
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Workflow</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateWorkflow} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Workflow Name *</label>
                <Input
                  data-testid="workflow-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Buyer Qualification"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Input
                  data-testid="workflow-description-input"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Brief description of this workflow"
                />
              </div>
              <Button
                data-testid="submit-workflow-button"
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600"
              >
                Create Workflow
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Workflow Templates */}
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-0 shadow-lg">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Workflow Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {workflowTemplates.map((template, index) => (
            <div
              key={index}
              className="p-5 bg-white rounded-lg shadow hover:shadow-md transition-all"
            >
              <WorkflowIcon className="w-8 h-8 text-purple-600 mb-3" />
              <h4 className="font-semibold text-slate-800 mb-2">{template.name}</h4>
              <p className="text-sm text-slate-600 mb-4">{template.description}</p>
              <div className="mb-4">
                <div className="text-xs text-slate-500 mb-2">
                  {template.nodes.length} steps
                </div>
                <div className="flex flex-wrap gap-1">
                  {template.nodes.slice(0, 3).map((node, i) => (
                    <span
                      key={i}
                      className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded"
                    >
                      {node.type}
                    </span>
                  ))}
                  {template.nodes.length > 3 && (
                    <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                      +{template.nodes.length - 3}
                    </span>
                  )}
                </div>
              </div>
              <Button
                onClick={() => createTemplateWorkflow(template)}
                size="sm"
                className="w-full"
                variant="outline"
              >
                Use Template
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Workflows Grid */}
      {workflows.length === 0 ? (
        <Card className="p-12 text-center">
          <WorkflowIcon className="w-16 h-16 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 mb-4">
            No workflows yet. Create your first workflow or use a template!
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-gradient-to-r from-blue-500 to-purple-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Workflow
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workflows.map((workflow) => (
            <Card
              key={workflow.id}
              data-testid={`workflow-card-${workflow.id}`}
              className="p-6 card-hover bg-white border-0 shadow-lg"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center shadow-lg">
                    <WorkflowIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">{workflow.name}</h3>
                    <span className="text-sm text-slate-600">
                      {workflow.nodes?.length || 0} nodes
                    </span>
                  </div>
                </div>
              </div>

              {workflow.description && (
                <p className="text-sm text-slate-600 mb-4">{workflow.description}</p>
              )}

              <div className="flex gap-2">
                <Button
                  data-testid={`edit-workflow-${workflow.id}`}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Test
                </Button>
                <Button
                  data-testid={`delete-workflow-${workflow.id}`}
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Workflows;